import {Dbg} from '../util.js';
import {Sysex} from '../sysex.js';
import {Constants} from "../constants.js";


// More recent Roland devices follow the same pattern:
// 0xf0, 0x41, channel, modelID, commandID

const rolandStandardHeader = [
  0xf0, // Start-of-sysex
  0x41, // Roland
  undefined, // device ID / channel (0-15)
  undefined, // model ID
  undefined, // command ID
];

const rolandStandardCommandIDs = {
  0x11: 'Data request', // one way transfer
  0x12: 'Data set', // one way transfer
  0x40: 'Send request',
  0x41: 'Data request', // handshake mode
  0x42: 'Data set', // handshake mode
  0x43: 'Acknowledge',
  0x45: 'End-of-data',
  0x4e: 'Communication error',
  0x4f: 'Rejection',
};

const rolandModelIDs = {
  0x14: 'D-50',
  0x16: 'D-20', // also: MT-32?
  0x3d: 'JD-800',
  0x42: 'GS', // used by several devices when in GS mode
  0x6a: 'JV-1080', // also: JV-1010/2080/XP-80
}


// Non-standard sysex headers

// Sysex data form early Roland devices appear to
// follow the pattern:
// 0xf0, 0x41, messageType, channel
// Since this doesn't contain model information, we can
// guess it based on messageType and total data size:
// [header, total size (bytes) if known, device, sysex type]
const legacyRolandHeaders = [
  // Juno-106
  [[0xf0, 0x41, 0x30], 19, 'Juno-106', 'Patch data'],
  [[0xf0, 0x41, 0x31], undefined, 'Juno-106', 'Manual mode'],
  [[0xf0, 0x41, 0x32], 7, 'Juno-106', 'Control change'],
  // JX-10
  [[0xf0, 0x41, 0x40, undefined, 24], undefined, 'JX-10', 'Send request'],
  [[0xf0, 0x41, 0x41, undefined, 24], undefined, 'JX-10', 'Data request'],
  [[0xf0, 0x41, 0x42, undefined, 24], 135, 'JX-10', 'Data'],
  [[0xf0, 0x41, 0x43, undefined, 24], 6, 'JX-10', 'Acknowledge'],
  [[0xf0, 0x41, 0x45, undefined, 24], 6, 'JX-10', 'End-of-file'],
  [[0xf0, 0x41, 0x4e, undefined, 24], 6, 'JX-10', 'Communication error'],
  [[0xf0, 0x41, 0x4f, undefined, 24], 6, 'JX-10', 'Rejection'],
  // MKS-70
  [[0xf0, 0x41, 0x34, undefined, 24], 11, 'MKS-70', 'Program number (patch)'],
  [[0xf0, 0x41, 0x34, undefined, 24], 11, 'MKS-70', 'Program number (Tone)'],
  [[0xf0, 0x41, 0x35, undefined, 24], 59, 'MKS-70', 'Patch data'],
  [[0xf0, 0x41, 0x35, undefined, 24], 67, 'MKS-70', 'Tone data'],
  [[0xf0, 0x41, 0x36, undefined, 24], 10, 'MKS-70', 'Patch parameter'],
  [[0xf0, 0x41, 0x36, undefined, 24], 10, 'MKS-70', 'Tone parameter'],
  [[0xf0, 0x41, 0x37, undefined, 24], 11, 'MKS-70', 'Patch bulk dump'],
  [[0xf0, 0x41, 0x37, undefined, 24], 11, 'MKS-70', 'Tone bulk dump'],

];

//
// const juno106ControlChangeHeader = [
//   0xf0, 0x41, // Roland
//   0x32, // Message type = Control Change
//   undefined, // channel
//   undefined, // control number
//   undefined]; // control value
// const juno106PatchDataHeader = [
//   0xf0, 0x41, // Roland
//   0x30, // Message type = Bank/Patch change (data transfer)
//   undefined, // channel
//   undefined]; // bank/patch number
// const juno106ManualMode = [
//   0xf0, 0x41, // Roland
//   0x31, // Message type = Manual mode
//   undefined, // channel
// ];

// const d50Header = [
//   0xf0,
//   0x41, // Roland
//   undefined, // MIDI basic channel (0-15)
//   0x14, // D-50
//   undefined]; // command ID
// const d50Commands = {
//   0x11: 'Data request', // one way transfer
//   0x12: 'Data set', // one way transfer
//   0x40: 'Send request',
//   0x41: 'Data request', // in bulk-load mode
//   0x42: 'Data set', // in bulk-load mode
//   0x43: 'Acknowledge',
//   0x45: 'End-of-data',
//   0x4e: 'Communication errors',
//   0x4f: 'Rejection',
// };
//
// const jd800Header = [
//   0xf0,
//   0x41, // Roland
//   undefined, // device id
//   0x3d, // JD-800
//   undefined]; // command ID
// const jd800Commands = {
//   0x11: 'Data request',
//   0x12: 'Data set'
// };
//
// const jv1010Header = [
//   0xf0,
//   0x41, // Roland
//   undefined, // device ID
//   0x6a, // JV-1010
//   undefined]; // command ID
// const jv1010Commands = {
//   0x11: 'Data request',
//   0x12: 'Data set'
// };


export function ParseRoland(sysex)
{
  if (!sysex) return;

  sysex.parseManufacturer();
  if (manufacturerName != 'Roland') {
    throw new Error('Not a Roland sysex');
  }

  // Special cases
  for (headerData of legacyRolandHeaders)
  {
    let [pattern, size, model, type] = headerData;
    if (Parser.matchesPattern(sysex.rawData, pattern))
    {
      if (size === undefined || size === sysex.rawData.byteLength) {
        sysex.device = model;
        sysex.type = type;
        sysex.isKnownType = true;
        return;
      }
    }
  }

  // if (Parser.matchesPattern(sysex.rawData, juno106ControlChangeHeader)) {
  //   sysex.device = 'Juno-106';
  //   sysex.isKnownType = true;
  //   sysex.type = 'Control change';
  // }
  // else if (Parser.matchesPattern(sysex.rawData, juno106PatchDataHeader)) {
  //   sysex.device = 'Juno-106';
  //   sysex.isKnownType = true;
  //   sysex.type = 'Patch data (bank/patch)';
  // }
  // else if (Parser.matchesPattern(sysex.rawData, juno106ManualMode)) {
  //   sysex.device = 'Juno-106';
  //   sysex.isKnownType = true;
  //   sysex.type = 'Patch data (manual mode)';
  // }

  // Standard Roland Sysex format.
  if (Parser.matchesPattern(sysex.rawData, rolandStandardHeader)) {
    let modelID = sysex.rawData[3];
    if (rolandModelIDs.hasOwnProperty(modelID))
    {
      sysex.device = rolandModelIDs[modelID];
      sysex.isKnownType = true;
      let commandID = sysex.rawData[4];
      if (rolandStandardCommandIDs.hasOwnProperty(commandID)) {
        sysex.type = rolandStandardCommandIDs[commandID];
      }
    }
  }




  // if (Parser.matchesPattern(sysex.rawData, d50Header)) {
  //   sysex.device = 'D-50';
  //   sysex.isKnownType = true;
  //   let commandId = sysex.rawData[4];
  //   ParseType(sysex, commandId, d50Commands);
  // }
  // if (Parser.matchesPattern(sysex.rawData, jd800Header)) {
  //   sysex.device = 'JD-800';
  //   sysex.isKnownType = true;
  //   let commandId = sysex.rawData[4];
  //   ParseType(sysex, commandId, jd800Commands);
  // }
  // if (Parser.matchesPattern(sysex.rawData, jv1010Header)) {
  //   sysex.device = 'JV-1010';
  //   sysex.isKnownType = true;
  //   let commandId = sysex.rawData[4];
  //   ParseType(sysex, commandId, jv1010Commands);
  // }

}

function ParseType(sysex, commandId, commands)
{
  if (commands.hasOwnProperty(commandId))
  {
    sysex.type = commands[commandId];
  }
}



// WEBPACK FOOTER //
// ./src/roland/roland.js